import { get, post, del } from '@/api/apiService.ts';
import apiClient from '@/api/apiClient.ts';
import type {
  CreateSimulationRequest,
  UpdateSimulationRequest,
  SimulationResponse,
  SimulationDocumentResponse,
  UploadConfig,
} from '@/types/simulation.ts';

function extractErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const axiosError = error as { response?: { data?: { message?: string; title?: string } } };
    const data = axiosError.response?.data;
    if (data?.message) return data.message;
    if (data?.title) return data.title;
  }
  if (error instanceof Error) return error.message;
  return 'Ha ocurrido un error inesperado';
}

// ── Multipart presigned upload (client → S3) ───────────────────────────────

interface PresignedPart {
  partNumber: number;
  url: string;
}

interface InitiateMultipartResponse {
  documentKey: string;
  uploadId: string;
  partSize: number;
  parts: PresignedPart[];
}

interface CompletedPart {
  partNumber: number;
  eTag: string;
}

// Upload config is a per-deployment constant; fetch it once and reuse.
let uploadConfigCache: Promise<UploadConfig> | null = null;

function fetchUploadConfig(simulationId: string): Promise<UploadConfig> {
  if (!uploadConfigCache) {
    uploadConfigCache = get<UploadConfig>(
      `/api/simulations/${simulationId}/documents/upload-config`,
    ).catch((error) => {
      // Don't cache a failure — allow a later retry.
      uploadConfigCache = null;
      throw error;
    });
  }
  return uploadConfigCache;
}

async function uploadViaMultipart(
  simulationId: string,
  file: File,
): Promise<SimulationDocumentResponse> {
  const init = await post<
    { fileName: string; contentType: string; fileSize: number },
    InitiateMultipartResponse
  >(`/api/simulations/${simulationId}/documents/multipart/initiate`, {
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
  });

  try {
    const completedParts: CompletedPart[] = [];
    for (const part of init.parts) {
      const start = (part.partNumber - 1) * init.partSize;
      const end = Math.min(start + init.partSize, file.size);
      const chunk = file.slice(start, end);

      // Direct PUT to S3 — raw fetch, NO Authorization header (the presigned
      // URL carries its own auth in the query string).
      const res = await fetch(part.url, { method: 'PUT', body: chunk });
      if (!res.ok) throw new Error('UPLOAD_PART_FAILED');

      // S3 returns the part ETag (quoted); CORS ExposeHeaders makes it readable.
      const eTag = res.headers.get('ETag') ?? res.headers.get('etag');
      if (!eTag) throw new Error('UPLOAD_PART_FAILED');

      completedParts.push({ partNumber: part.partNumber, eTag });
    }

    return await post<
      {
        documentKey: string;
        uploadId: string;
        fileName: string;
        contentType: string;
        fileSize: number;
        parts: CompletedPart[];
      },
      SimulationDocumentResponse
    >(`/api/simulations/${simulationId}/documents/multipart/complete`, {
      documentKey: init.documentKey,
      uploadId: init.uploadId,
      fileName: file.name,
      contentType: file.type,
      fileSize: file.size,
      parts: completedParts,
    });
  } catch (error) {
    // Best-effort abort so we don't leave a dangling multipart upload.
    try {
      await post(`/api/simulations/${simulationId}/documents/multipart/abort`, {
        documentKey: init.documentKey,
        uploadId: init.uploadId,
      });
    } catch {
      /* ignore abort failures */
    }
    throw error;
  }
}

async function uploadViaServer(
  simulationId: string,
  file: File,
): Promise<SimulationDocumentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<SimulationDocumentResponse>(
    `/api/simulations/${simulationId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

const simulationService = {
  getAll(): Promise<SimulationResponse[]> {
    return get<SimulationResponse[]>('/api/simulations');
  },

  getById(id: string): Promise<SimulationResponse> {
    return get<SimulationResponse>(`/api/simulations/${id}`);
  },

  async create(data: CreateSimulationRequest): Promise<SimulationResponse> {
    try {
      const response = await apiClient.post<SimulationResponse>('/api/simulations', data);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  async update(id: string, data: UpdateSimulationRequest): Promise<SimulationResponse> {
    try {
      const response = await apiClient.put<SimulationResponse>(`/api/simulations/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  delete(id: string): Promise<void> {
    return del<void>(`/api/simulations/${id}`);
  },

  getMyAssignments(): Promise<SimulationResponse[]> {
    return get<SimulationResponse[]>('/api/simulations/my-assignments');
  },

  async uploadDocument(simulationId: string, file: File): Promise<SimulationDocumentResponse> {
    try {
      const config = await fetchUploadConfig(simulationId);
      return config.directClientUpload
        ? await uploadViaMultipart(simulationId, file)
        : await uploadViaServer(simulationId, file);
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  getDocuments(simulationId: string): Promise<SimulationDocumentResponse[]> {
    return get<SimulationDocumentResponse[]>(`/api/simulations/${simulationId}/documents`);
  },

  async getDocumentDownloadUrl(simulationId: string, documentId: string): Promise<string> {
    const { url } = await get<{ url: string }>(
      `/api/simulations/${simulationId}/documents/${documentId}/download-url`,
    );
    return url;
  },

  deleteDocument(simulationId: string, documentId: string): Promise<void> {
    return del<void>(`/api/simulations/${simulationId}/documents/${documentId}`);
  },
};

export default simulationService;
